/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ActionButton, IconButton } from '@fluentui/react/lib/Button';
import { IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { FontIcon } from '@fluentui/react/lib/Icon';
import { Stack } from '@fluentui/react/lib/Stack';
import { Toggle } from '@fluentui/react/lib/Toggle';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logQnaKBButtonClick, logToggleChange } from 'src/core/services/telemetry/eventsNamesTemplate';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { LuisKeyCodes } from 'src/core/utils/keyCodes';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { QnaMakerQnaPairAlternateQuestionsSection } from 'src/qnaMaker/appShell/qnaMakerEditPage/components/QnaMakerQnaPairAlternateQuestionsSection';
import { QnaMakerQnaPairAnswerSection } from 'src/qnaMaker/appShell/qnaMakerEditPage/components/QnaMakerQnaPairAnswerSection';
import { QnaMakerQnaPairContextPanel } from 'src/qnaMaker/appShell/qnaMakerEditPage/components/QnaMakerQnaPairContextPanel';
import { QnaMakerQnaPairFollowUpPromptsSection } from 'src/qnaMaker/appShell/qnaMakerEditPage/components/QnaMakerQnaPairFollowupPromptsSection';
import { QnaMakerQnaPairMetadataSection } from 'src/qnaMaker/appShell/qnaMakerEditPage/components/QnaMakerQnaPairMetadataSection';
import { QnaMakerQnaPairsUiStore } from 'src/qnaMaker/appShell/qnaMakerEditPage/QnaMakerQnaPairsUiStore';
import { QnaMakerQnaSwagger } from 'src/qnaMaker/core/services/qnas/QnaMakerAssetsSwaggers';
import { QnaMakerQnaStore } from 'src/qnaMaker/core/stores/QnaMakerQnaStore';
import { qnaMakerQnaStoreToQnaSwagger } from 'src/qnaMaker/modules/handlers/qnas/qnaMakerAssetsMappers';
import { useQnaPairsActions } from 'src/qnaMaker/modules/hooks/useQnaPairsActions';

type QnaMakerQnaPairDetailsProps = {
    qnaPairData: QnaMakerQnaStore;
    qnaUiStore: QnaMakerQnaPairsUiStore;
};

const messages = defineMessages({
    enableRichText: {
        id: 'qnaMaker.qnaMakerQnaPairDetails.enableRichText',
        defaultMessage: 'Enable rich text'
    },
    disableRichText: {
        id: 'qnaMaker.qnaMakerQnaPairDetails.disableRichText',
        defaultMessage: 'Disable rich text'
    },
    showContextTree: {
        id: 'qnaMaker.qnaMakerQnaPairDetails.showContextTree',
        defaultMessage: 'Show context tree'
    },
    source: {
        id: 'qnaMaker.qnaMakerQnaPairDetails.source',
        defaultMessage: 'Source:'
    },
    textPlaceholder: {
        id: 'qnaMaker.qnaMakerQnaPairDetails.textPlaceholder',
        defaultMessage: 'Add question and press enter'
    },
    edit: {
        id: 'qnaMaker.qnaMakerQnaPairDetails.edit',
        defaultMessage: 'Edit'
    },
    sourcePlaceholder: {
        id: 'qnaMaker.qnaMakerQnaPairDetails.sourcePlaceholder',
        defaultMessage: 'Select a source'
    }
});

export const QnaMakerQnaPairDetails = observer((props: QnaMakerQnaPairDetailsProps) => {
    const { qnaPairData, qnaUiStore } = props;
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();
    const { serviceName } = getPortalFromUrl();

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite } = mapUserRoleToUserActions(userRole);

    const {
        id: currentProjectId,
        languageDirection,
        sourcesStoreHouse: { items: sourceStores }
    } = rootStore.currentQnaMakerProjectStore;

    const { isRichTextEnabled, qnaPairsBeingEditedStore, displayedQnaPairStores } = qnaUiStore;

    const displayedSources: IDropdownOption[] = sourceStores.map(s => ({ key: s.source, text: s.source }));
    if (!displayedSources.some(src => src.key === 'Editorial')) {
        displayedSources.unshift({ key: 'Editorial', text: 'Editorial' });
    }

    const { editQnaPair } = useQnaPairsActions(qnaUiStore);

    const [isContextPanelOpen, setIsContextPanelOpen] = React.useState(false);

    const [isDefaultQuestionInEditMode, setIsDefaultQuestionInEditMode] = React.useState(false);
    const [editableDefaultQuestion, setEditableDefaultQuestion] = React.useState(qnaPairData?.questions[0]);

    React.useEffect(() => setEditableDefaultQuestion(qnaPairData?.questions[0]), [qnaPairData?.questions[0]]);

    const [isSourceInEditMode, setIsSourceInEditMode] = React.useState(false);
    const [selectedSource, setSelectedSource] = React.useState(displayedSources.find(s => s.key === qnaPairData?.source));

    React.useEffect(() => setSelectedSource(displayedSources.find(s => s.key === qnaPairData?.source)), [qnaPairData?.source]);

    const handleRichText = () => {
        logToggleChange(serviceName, 'NewEditKb', 'EnableRichText', isRichTextEnabled ? 'Off' : 'On', 'Qnas', currentProjectId);
        qnaUiStore.toggleIsRichTextEnabled();
    };

    const handleContextTree = () => {
        logQnaKBButtonClick(serviceName, 'ShowContext', 'Button', 'Open', currentProjectId);
        setIsContextPanelOpen(true);
    };

    const handleEditQna = (buttonPlace: string, section: string, action: string) => {
        logQnaKBButtonClick(serviceName, buttonPlace, section, action, 'Qnas', currentProjectId);
        if (!qnaPairsBeingEditedStore.hasPair(qnaUiStore.selectedQnaId)) {
            qnaPairsBeingEditedStore.addPair(
                qnaMakerQnaStoreToQnaSwagger(displayedQnaPairStores.find(q => q.id === qnaUiStore.selectedQnaId))
            );
        }
    };

    const handleSubmitEditQnaPair = (newData: Partial<QnaMakerQnaSwagger>, qnaId?: number) => {
        const id = qnaId ?? qnaUiStore.selectedQnaId;
        editQnaPair(id, newData);

        if (qnaPairsBeingEditedStore.hasPair(id)) {
            qnaPairsBeingEditedStore.deletePair(id);
        }
    };

    const handleQnaContextualNavigation = (id: number) => {
        setIsContextPanelOpen(false);
        qnaUiStore.setSelectedQnaId(id);
    };

    const updateDefaultQuestion = () => {
        setIsDefaultQuestionInEditMode(false);
        const newQuestions = [...qnaPairData?.questions];
        newQuestions[0] = editableDefaultQuestion;
        if (newQuestions[0] !== qnaPairData?.questions[0]) {
            handleSubmitEditQnaPair({ questions: newQuestions });
        }
    };

    const handleOnDefaultKeyUp = (e: React.KeyboardEvent<HTMLElement | HTMLTextAreaElement>) => {
        if (e.key === LuisKeyCodes.Enter) {
            updateDefaultQuestion();
        }

        if (e.key === LuisKeyCodes.Escape) {
            setEditableDefaultQuestion(qnaPairData?.questions[0]);
            setIsDefaultQuestionInEditMode(false);
        }
    };

    const updateSource = () => {
        if (isSourceInEditMode) {
            if (String(selectedSource.key) !== qnaPairData?.source) {
                handleSubmitEditQnaPair({ source: String(selectedSource.key) });
            }
        }
        setIsSourceInEditMode(!isSourceInEditMode);
    };

    return (
        <Stack tokens={{ childrenGap: 20, padding: '0 0 0 28px' }} styles={{ root: { marginTop: '10px' } }}>
            <Stack horizontal horizontalAlign="space-between">
                {isDefaultQuestionInEditMode ? (
                    <LuisTextField
                        required
                        autoFocus
                        onKeyUp={handleOnDefaultKeyUp}
                        dir={languageDirection}
                        value={editableDefaultQuestion}
                        onBlur={updateDefaultQuestion}
                        onChange={(_, v) => setEditableDefaultQuestion(v)}
                        styles={{ root: { width: '500px' } }}
                        placeholder={localize(messages.textPlaceholder)}
                    />
                ) : (
                    <Stack onClick={() => setIsDefaultQuestionInEditMode(true)}>
                        <LuisText fontWeight={600} size="24px">
                            {qnaPairData?.questions[0]}
                        </LuisText>
                    </Stack>
                )}
                <Stack horizontal horizontalAlign="end" tokens={{ childrenGap: 5, padding: '0 10px 0 0' }}>
                    <Toggle
                        onChange={handleRichText}
                        checked={isRichTextEnabled}
                        styles={{ root: { paddingTop: '12px' } }}
                        onText={localize(messages.disableRichText)}
                        offText={localize(messages.enableRichText)}
                    />
                    <ActionButton iconProps={{ iconName: 'Org' }} onClick={handleContextTree} text={localize(messages.showContextTree)} />
                </Stack>
            </Stack>
            <Stack horizontal tokens={{ childrenGap: 20 }} verticalAlign="center">
                <Stack horizontal tokens={{ childrenGap: 5 }}>
                    <FontIcon iconName="Source" />
                    {isSourceInEditMode ? (
                        <LuisDropdown
                            ariaLabel={localize(messages.sourcePlaceholder)}
                            required
                            options={displayedSources}
                            selectedKey={selectedSource?.key}
                            styles={{ root: { width: 300 } }}
                            placeholder={localize(messages.sourcePlaceholder)}
                            onChange={(_, option) => setSelectedSource(option)}
                        />
                    ) : (
                        <LuisText>
                            {localize(messages.source)} {qnaPairData?.source}
                        </LuisText>
                    )}
                </Stack>
                <RBACTooltip
                    hidden={canWrite}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="editSourceLanguageToolTip"
                    componentSurroundedByToolTip={
                        <IconButton
                            title={localize(messages.edit)}
                            ariaLabel={localize(messages.edit)}
                            disabled={!canWrite}
                            iconProps={{
                                iconName: `${isSourceInEditMode ? 'CheckMark' : 'Edit'}`,
                                styles: { root: { cursor: 'pointer' } }
                            }}
                            onClick={updateSource}
                        />
                    }
                />
            </Stack>
            <QnaMakerQnaPairAnswerSection
                answer={qnaPairData?.answer}
                handleEditQna={handleEditQna}
                isRichTextEnabled={isRichTextEnabled}
                handleSubmitEditQnaPair={handleSubmitEditQnaPair}
            />
            <QnaMakerQnaPairAlternateQuestionsSection
                questions={qnaPairData?.questions}
                handleEditAlternateQuestions={handleEditQna}
                handleSubmitAlternateQuestions={handleSubmitEditQnaPair}
            />
            <QnaMakerQnaPairFollowUpPromptsSection
                handleEditPrompts={handleEditQna}
                prompts={qnaPairData?.dialog.prompts}
                qnaPairsJson={qnaUiStore.qnaPairsJson}
                selectedQnaId={qnaUiStore.selectedQnaId}
                handleSubmitPrompts={handleSubmitEditQnaPair}
                isContextOnly={qnaPairData?.dialog.isContextOnly}
            />
            <QnaMakerQnaPairMetadataSection
                metadata={qnaPairData?.metadata}
                handleEditMetadata={handleEditQna}
                handleSubmitMetadata={handleSubmitEditQnaPair}
            />
            <QnaMakerQnaPairContextPanel
                qnaId={qnaPairData?.id}
                isOpen={isContextPanelOpen}
                contextTreeMap={qnaUiStore.contextTreeMap}
                onDismiss={() => setIsContextPanelOpen(false)}
                isContextOnly={qnaPairData?.dialog.isContextOnly}
                handleQnaContextualNavigation={handleQnaContextualNavigation}
                isContextTreeCalculating={qnaUiStore.isContextTreeCalculating}
            />
        </Stack>
    );
});
